import * as React from 'react'
import {SingleBox as Select} from "../../lib/sbhtml/select";
import {DatePicker} from "../../lib/sbhtml/datePicker";
import {
	DateType,
	GeoState,
	GeoStates, isDateType, isOrderStatus, isPaidStatus, OrderStatus, Origin, Origins, PaidStatus, PaymentGroup,
	PaymentGroups,
	PaymentMethod,
	PaymentMethods
} from "./data";
import {dateToTimestamp, reduceObj} from "../../lib/utils";
import {AlwaysPost} from "../../lib/sbhtml/input";

export type OrdersFiltersProps = ValueProps & CallbackProps

export type ValueProps = {
	selectedOrderStatus: OrderStatus
	selectedPaidStatus: PaidStatus
	selectedPaymentMethod: PaymentMethod
	selectedOrigin: Origin
	selectedState: GeoState
	selectedPaymentGroup: PaymentGroup
	selectedDateType: DateType
	fromDate: Date | null
	toDate: Date | null

	paymentMethods: PaymentMethods
	origins: Origins
	states: GeoStates
	paymentGroups: PaymentGroups
}

export type CallbackProps = {
	setOrderStatus: (orderStatus: OrderStatus) => void
	setPaidStatus: (paidStatus: PaidStatus) => void
	setPaymentMethod: (paymentMethod: PaymentMethod) => void
	setOrigin: (origin: Origin) => void
	setState: (state: GeoState) => void
	setPaymentGroup: (paymentGroup: PaymentGroup) => void
	setDateType: (dateType: DateType) => void
	setFromDate: (fromDate: Date) => void
	setToDate: (toDate: Date) => void
	clearFromDate: () => void
	clearToDate: () => void
}

export const OrdersFilters = (props: OrdersFiltersProps) => {
	const style = (backgroundColor: string) => ({
		style: {
			backgroundColor: backgroundColor,
			color: "#FFFFFF"
		}
	})

	const enabledGeneral = props.selectedOrderStatus !== 'any'
	const disabledDate = props.selectedDateType === 'any'

	const fromDateProps = props.fromDate !== null ? {date: new Date(props.fromDate)} : {}
	const toDateProps = props.toDate !== null ? {date: new Date(props.toDate)} : {}

	return <div>
		<AlwaysPost name="o_orderStatus" value={props.selectedOrderStatus}/>
		<AlwaysPost name="o_paidStatus" value={props.selectedPaidStatus}/>
		<AlwaysPost name="o_paymentMethod" value={props.selectedPaymentMethod}/>
		<AlwaysPost name="o_origin" value={props.selectedOrigin}/>
		<AlwaysPost name="o_state" value={props.selectedState}/>
		<AlwaysPost name="o_paymentGroup" value={props.selectedPaymentGroup}/>
		<AlwaysPost name="o_dateType" value={props.selectedDateType}/>
		<AlwaysPost name="o_fromDate" value={dateToTimestamp(props.fromDate)}/>
		<AlwaysPost name="o_toDate" value={dateToTimestamp(props.toDate)}/>
		<label>
			<p>Order Status:</p>
			<Select
				value={props.selectedOrderStatus}
				onChange={os => props.setOrderStatus(isOrderStatus(os) ? os : 'any')}
			>
				<option value="any"/>
				<option value="current" >Current</option>
				<option value="-3">All</option>
				<option value="0" {...style('darkred')}>Unprocessed</option>
				<option value="-2" {...style('darkorange')}>Processing</option>
				<option value="1" {...style('darkgreen')}>Processed</option>
				<option value="-1" {...style('darkblue')}>Archived</option>
				<option value="2" {...style('purple')}>Refunded</option>
				<option value="3" {...style('gray')}>Superseded</option>
				<option value="4" {...style('dimgray')}>Cancelled</option>
			</Select>
		</label>
		<label>
			<p>Paid Status:</p>
			<Select
				value={props.selectedPaidStatus}
				onChange={ps => props.setPaidStatus(isPaidStatus(ps) ? ps : 'any')}
				enabled={enabledGeneral}
			>
				<option value="any">Any</option>
				<option value="pending">Pending Payment</option>
				<option value="paid">Paid</option>
			</Select>
		</label>
		<label>
			<p>Payment Method:</p>
			<Select
				value={props.selectedPaymentMethod}
				onChange={pm => props.setPaymentMethod(pm)}
				enabled={enabledGeneral}
			>
				<option value="any">Any</option>
				{reduceObj((k: string, v: string, acc: JSX.Element[]) => {
					acc.push(<option key={"pm-" + k} value={k}>{v}</option>)
					return acc
				}, props.paymentMethods, [])}
			</Select>
		</label>
		<label>
			<p>Origin:</p>
			<Select
				value={props.selectedOrigin}
				onChange={o => props.setOrigin(o)}
				enabled={enabledGeneral}
			>
				<option value="any">Any</option>
				{reduceObj((k: string, v: string, acc: JSX.Element[]) => {
					acc.push(<option key={"o-" + k} value={k}>{v}</option>)
					return acc
				}, props.origins, [])}
			</Select>
		</label>
		<label>
			<p>State:</p>
			<Select
				value={props.selectedState}
				onChange={s => props.setState(s)}
				enabled={enabledGeneral}
			>
				<option value="any">Any</option>
				{reduceObj((k: string, v: string, acc: JSX.Element[]) => {
					acc.push(<option key={"s-" + k} value={k}>{v}</option>)
					return acc
				}, props.states, [])}
			</Select>
		</label>
		<label>
			<p>Payment Group:</p>
			<Select
				value={props.selectedPaymentGroup}
				onChange={pg => props.setPaymentGroup(pg)}
				enabled={enabledGeneral}
			>
				<option value="any">Any</option>
				{reduceObj((k: string, v: string, acc: JSX.Element[]) => {
					acc.push(<option key={"pg-" + k} value={k}>{v}</option>)
					return acc
				}, props.paymentGroups, [])}
			</Select>
		</label>
		<p>Date(s):</p>
		<div style={{marginLeft: '25px'}}>
			<label>
				<p>Type:</p>
				<Select
					value={props.selectedDateType}
					onChange={dt => props.setDateType(isDateType(dt) ? dt : 'any')}
					enabled={enabledGeneral}
				>
					<option value="any"/>
					<option value="submittedDate">Submitted Date</option>
					<option value="processedDate">Processed Date</option>
					<option value="paymentReceivedDate">Payment Received Date</option>
				</Select>
			</label>
			<label>
				<p>From:</p>
				<DatePicker
					{...fromDateProps}
					onChange={(e,d) => props.setFromDate(d)}
					disabled={disabledDate}
					readonly={false}
					invalid={false}
					hasClearButton={props.clearFromDate}
				/>
			</label>
			<label>
				<p>To:</p>
				<DatePicker
					{...toDateProps}
					onChange={(e,d) => props.setToDate(d)}
					disabled={disabledDate}
					readonly={false}
					invalid={false}
					hasClearButton={props.clearToDate}
				/>
			</label>
		</div>
	</div>
}



// WEBPACK FOOTER //
// ./src/orders2/orders-filters/presentation.tsx